/* eslint-disable react/prop-types */
/**
 * Iter 228 — NavigationPrefsCard
 *
 * Per-user customisation of:
 *   • Mobile bottom-nav (4-6 slots, drag-reorder + show/hide)
 *   • Home Quick Actions row (order + show/hide)
 *
 * Stored via PUT /api/me/prefs/nav. Falls back to role default when
 * empty. Filters the picker by the user's perms (no point letting a
 * tech pin "Pipeline" — they can't open it).
 */
import { useEffect, useState } from "react";
import { Check, Loader2, RotateCcw, Smartphone, Plus } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

// Mirror of QuickActionsLauncher catalogue + bottom-nav slot catalogue.
const BOTTOM_NAV_POOL = [
  { key: "jobcards",  label: "Jobs",       perm: "nav.jobcards" },
  { key: "tickets",   label: "Tickets",    perm: "nav.tickets" },
  { key: "tasks",     label: "Tasks",      perm: "nav.tasks" },
  { key: "customers", label: "Customers",  perm: "nav.customers" },
  { key: "calendar",  label: "Calendar",   perm: "nav.calendar" },
  { key: "chat",      label: "Chat",       perm: "nav.dashboard" },
  { key: "leads",     label: "Leads",      perm: "nav.leads" },
  { key: "pipeline",  label: "Pipeline",   perm: "nav.sales_funnel" },
  { key: "projects",  label: "Projects",   perm: "nav.jobcards" },
  { key: "picking",   label: "Picking",    perm: "nav.tools" },
  { key: "inventory", label: "Inventory",  perm: "nav.equipment" },
  { key: "suppliers", label: "Suppliers",  perm: "nav.tools" },
  { key: "orders",    label: "Orders",     perm: "nav.orders" },
  { key: "invoices",  label: "Invoices",   perm: "nav.invoices" },
];

const QUICK_ACTIONS_POOL = [
  { key: "clock",       label: "Clock in / out",   perm: "nav.dashboard" },
  { key: "new_ticket",  label: "+ Ticket",          perm: "action.edit_tickets" },
  { key: "new_jc",      label: "+ Job card",        perm: "action.edit_jobcards" },
  { key: "new_lead",    label: "+ Lead",            perm: "action.edit_leads" },
  { key: "new_task",    label: "+ Task",            perm: "nav.tasks" },
  { key: "camera",      label: "Take photo",        perm: "nav.dashboard" },
  { key: "voice_note",  label: "Voice note",        perm: "ai.hidro_voice" },
  { key: "scan_slip",   label: "Scan slip",         perm: "tools.ocr" },
  { key: "eng_desk",    label: "Engineering Desk",  perm: "nav.tools" },
  { key: "picking",     label: "Picking",           perm: "nav.tools" },
];

const MIN_BN = 4;
const MAX_BN = 6;

export default function NavigationPrefsCard() {
  const { user, hasPermission } = useAuth();
  const [prefs, setPrefs] = useState({ bottom_nav: [], quick_actions: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/me/prefs/nav").then(({ data }) => {
      setPrefs(data || { bottom_nav: [], quick_actions: [] });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const toggleSlot = (poolKey, key) => {
    setPrefs((p) => {
      const list = [...(p[poolKey] || [])];
      const i = list.indexOf(key);
      if (i >= 0) list.splice(i, 1);
      else {
        if (poolKey === "bottom_nav" && list.length >= MAX_BN) return p;
        list.push(key);
      }
      return { ...p, [poolKey]: list };
    });
    setSaved(false);
    setError("");
  };

  const move = (poolKey, key, dir) => {
    setPrefs((p) => {
      const list = [...(p[poolKey] || [])];
      const i = list.indexOf(key);
      if (i < 0) return p;
      const j = dir === "up" ? i - 1 : i + 1;
      if (j < 0 || j >= list.length) return p;
      [list[i], list[j]] = [list[j], list[i]];
      return { ...p, [poolKey]: list };
    });
    setSaved(false);
  };

  const save = async () => {
    setError("");
    if (prefs.bottom_nav.length && prefs.bottom_nav.length < MIN_BN) {
      setError(`Bottom nav needs at least ${MIN_BN} items (or 0 to reset to role default).`);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/me/prefs/nav", {
        bottom_nav: prefs.bottom_nav,
        quick_actions: prefs.quick_actions,
      });
      setPrefs(data);
      // Notify Layout to re-read in this tab without a full reload
      window.dispatchEvent(new CustomEvent("hv:nav-prefs:updated", { detail: data }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/prefs/nav", { bottom_nav: [], quick_actions: [] });
      setPrefs(data);
      window.dispatchEvent(new CustomEvent("hv:nav-prefs:updated", { detail: data }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="hv-card p-5">
        <Loader2 className="w-4 h-4 animate-spin text-[#a0aabf]" />
      </div>
    );
  }

  const renderPool = (pool, poolKey, max) => {
    const eligible = pool.filter((it) => hasPermission(it.perm));
    const active = prefs[poolKey] || [];
    return (
      <div className="space-y-2.5">
        <div className="flex items-center justify-between text-xs text-[#a0aabf]">
          <span>
            Selected: <span className="text-white font-semibold">{active.length}</span>
            {max ? <span> / {max}</span> : null}
          </span>
          {active.length === 0 && (
            <span className="text-[10px] uppercase tracking-wider text-[#647087]">
              empty = uses role default
            </span>
          )}
        </div>
        {/* Active list — ordered, each row has up/down + remove */}
        {active.length > 0 && (
          <div className="space-y-1.5">
            {active.map((key, idx) => {
              const item = pool.find((p) => p.key === key);
              if (!item) return null;
              return (
                <div
                  key={key}
                  data-testid={`navpref-active-${poolKey}-${key}`}
                  className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/[0.04] border border-white/10 text-sm"
                >
                  <span className="text-[10px] font-mono text-[#647087] w-4 text-right">{idx + 1}</span>
                  <span className="flex-1">{item.label}</span>
                  <button
                    type="button"
                    onClick={() => move(poolKey, key, "up")}
                    disabled={idx === 0}
                    className="text-xs text-[#a0aabf] hover:text-white disabled:opacity-30 px-1"
                    aria-label="Move up"
                  >↑</button>
                  <button
                    type="button"
                    onClick={() => move(poolKey, key, "down")}
                    disabled={idx === active.length - 1}
                    className="text-xs text-[#a0aabf] hover:text-white disabled:opacity-30 px-1"
                    aria-label="Move down"
                  >↓</button>
                  <button
                    type="button"
                    onClick={() => toggleSlot(poolKey, key)}
                    className="text-xs text-rose-300 hover:text-rose-200 px-1.5"
                    data-testid={`navpref-remove-${poolKey}-${key}`}
                  >Remove</button>
                </div>
              );
            })}
          </div>
        )}
        {/* Pool — items not selected; click to add */}
        <div className="flex flex-wrap gap-1.5">
          {eligible.filter((it) => !active.includes(it.key)).map((it) => (
            <button
              key={it.key}
              type="button"
              onClick={() => toggleSlot(poolKey, it.key)}
              data-testid={`navpref-add-${poolKey}-${it.key}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/[0.03] border border-white/10 text-xs text-[#a0aabf] hover:text-white hover:bg-white/5 transition-colors"
            >
              <Plus className="w-3 h-3" /> {it.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="hv-card p-5" data-testid="navigation-prefs-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-[#1fb6c1]/15 border border-[#1fb6c1]/40 flex items-center justify-center">
            <Smartphone className="w-5 h-5 text-[#1fb6c1]" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold">Customise navigation</h2>
            <p className="text-sm text-[#a0aabf] mt-1 max-w-lg">
              Pin what you tap every day. Leave empty to use the default for
              your role ({user?.role || "user"}).
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {saved ? (
            <span className="text-xs text-emerald-300 inline-flex items-center gap-1" data-testid="navpref-saved-tick">
              <Check className="w-3 h-3" /> Saved
            </span>
          ) : null}
          <button
            type="button"
            onClick={reset}
            disabled={saving}
            data-testid="navpref-reset-btn"
            className="hv-btn hv-btn-ghost text-xs"
            title="Reset to role default"
          >
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="navpref-save-btn"
            className="hv-btn hv-btn-primary text-xs"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            Save
          </button>
        </div>
      </div>

      {error ? (
        <div className="mb-3 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200" data-testid="navpref-error">
          {error}
        </div>
      ) : null}

      <div className="grid md:grid-cols-2 gap-6">
        <section>
          <h3 className="text-sm font-semibold mb-2 text-white">Mobile bottom navigation</h3>
          <p className="text-xs text-[#647087] mb-3">Pick 4-6 destinations for the bottom bar on phones.</p>
          {renderPool(BOTTOM_NAV_POOL, "bottom_nav", MAX_BN)}
        </section>
        <section>
          <h3 className="text-sm font-semibold mb-2 text-white">Quick Actions ⊕</h3>
          <p className="text-xs text-[#647087] mb-3">Pin shortcuts to the ⊕ menu (top bar) and Home row.</p>
          {renderPool(QUICK_ACTIONS_POOL, "quick_actions", null)}
        </section>
      </div>
    </div>
  );
}
